import { createTextLayer, getLayer, updateLayer } from '../core/model/document';
import { insertNextTo, leafLayers } from '../core/model/tree';
import type { EditorDocument, TextLayer } from '../core/model/types';
import type { TextDraft } from '../state/editorStore';
import type { AppContext } from './context';

/**
 * The text tool's flow.
 *
 * A draft is edited outside the document and committed once, so typing a
 * paragraph is one undo step and abandoning it leaves nothing behind. That is
 * also why the draft carries its own style: the layer it becomes may not exist
 * yet.
 */

/** Topmost text layer whose box contains the point, if any. */
export function textLayerAt(doc: EditorDocument, x: number, y: number): TextLayer | null {
  // Text can live inside a group, so the search walks the leaves of the tree.
  const leaves = leafLayers(doc.layers);
  for (let index = leaves.length - 1; index >= 0; index -= 1) {
    const layer = leaves[index];
    if (layer?.type !== 'text' || !layer.visible) continue;
    const width = layer.maxWidth ?? Math.max(60, layer.text.length * layer.fontSize * 0.6);
    const height = Math.max(
      layer.fontSize,
      layer.text.split('\n').length * layer.fontSize * layer.lineHeight,
    );
    if (x >= layer.x && x <= layer.x + width && y >= layer.y && y <= layer.y + height) {
      return layer;
    }
  }
  return null;
}

export function draftFromLayer(layer: TextLayer): TextDraft {
  return {
    layerId: layer.id,
    x: layer.x,
    y: layer.y,
    text: layer.text,
    fontSize: layer.fontSize,
    fontFamily: layer.fontFamily,
    bold: layer.fontWeight === 'bold',
    italic: layer.italic,
    color: layer.color,
    lineHeight: layer.lineHeight,
  };
}

/** Commit the draft: one history entry, whether new or edited. */
export function commitTextDraft(ctx: AppContext, draft: TextDraft): void {
  const { doc, activeLayerId, commit, setActiveLayer, notify } = ctx.store.getState();
  const trimmed = draft.text;

  if (draft.layerId) {
    const existing = getLayer(doc, draft.layerId);
    if (!existing) {
      notify('That text layer is gone.', 'warn');
      return;
    }
    const patch: Partial<TextLayer> = {
      text: trimmed,
      x: draft.x,
      y: draft.y,
      fontSize: draft.fontSize,
      fontFamily: draft.fontFamily,
      fontWeight: draft.bold ? 'bold' : 'normal',
      italic: draft.italic,
      color: draft.color,
    };
    commit('Text', updateLayer(doc, draft.layerId, patch as Partial<TextLayer>));
    return;
  }

  if (trimmed.trim().length === 0) return; // nothing was typed: add nothing

  const layer = createTextLayer({
    name: trimmed.split('\n')[0]?.slice(0, 24) || 'Text',
    text: trimmed,
    x: draft.x,
    y: draft.y,
    fontSize: draft.fontSize,
    fontFamily: draft.fontFamily,
    fontWeight: draft.bold ? 'bold' : 'normal',
    italic: draft.italic,
    color: draft.color,
  });
  commit('Add Text', { ...doc, layers: insertNextTo(doc.layers, layer, activeLayerId, 1) });
  setActiveLayer(layer.id);
}
